import { Component, Input, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ImportDataService } from 'app/core/services/import-data.service';
import { concatMap, delay, from, of, tap } from 'rxjs';

@Component({
  selector: 'modal-import-scale-combined',
  templateUrl: './modal-import-scale-combined.component.html',
  styleUrls: ['./modal-import-scale-combined.component.scss']
})
export class ModalImportScaleCombinedComponent implements OnInit, AfterViewInit {

  isLoading    = false;
  formatError  = false;
  isInserting  = false;
  isImported   = false;
  displayTable = true;
  insertionMessage: string = null;

  @Input() importedData: any[] = [];
  selectedRows: any[] = [];
  dataSource = new MatTableDataSource<any>([]);

  displayedColumns = [
    'select', 'rowType',
    'name', 'description', 'isDailyRate', 'isMinimumWage', 'isAllowance',
    'ratePerPeriod', 'monthlyRate', 'dailyRate', 'hourlyRate', 'minuteRate',
    'hoursPerDay', 'minValue', 'maxValue', 'validFrom', 'validTo'
  ];

  // name being present = header row; absence = line row
  expectedHeaders = [
    'name', 'isDailyRate', 'isMinimumWage', 'isAllowance',
    'ratePerPeriod', 'monthlyRate', 'dailyRate',
    'hourlyRate', 'minuteRate', 'minValue', 'maxValue', 'validFrom', 'validTo'
  ];

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    public activeModal: NgbActiveModal,
    private importDataService: ImportDataService
  ) {}

  ngOnInit(): void {
    // Derive row type
    this.importedData.forEach(row => {
      const hasLine = row.ratePerPeriod?.toString().trim() || row.monthlyRate?.toString().trim();
      if (row.name?.trim()) {
        row._rowType = hasLine ? 'header+line' : 'header';
      } else {
        row._rowType = 'line';
      }
    });
    this.dataSource = new MatTableDataSource(this.importedData);
    this.selectedRows = [...this.importedData];
    if (!this.isDataFormatCorrect()) this.formatError = true;
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  isDataFormatCorrect(): boolean {
    if (!this.importedData.length) return true;
    const keys = Object.keys(this.importedData[0]);
    return this.expectedHeaders.every(h => keys.includes(h));
  }

  onCheckboxChange(row: any, checked: boolean): void {
    if (checked) { this.selectedRows.push(row); }
    else {
      const i = this.selectedRows.indexOf(row);
      if (i !== -1) this.selectedRows.splice(i, 1);
    }
  }

  isSelected(row: any): boolean { return this.selectedRows.includes(row); }

  toggleSelectAll(checked: boolean): void {
    this.selectedRows = checked ? [...this.importedData] : [];
  }

  save(): void {
    // Build scale groups: header row starts a scale, line rows attach to the last header.
    // Header rows that also carry rate data are treated as the first line too.
    const groups: Array<{ header: any; lines: any[] }> = [];
    let current: { header: any; lines: any[] } | null = null;

    this.selectedRows.forEach(row => {
      if (row._rowType === 'header' || row._rowType === 'header+line') {
        current = { header: row, lines: [] };
        groups.push(current);
        if (row._rowType === 'header+line') current.lines.push(row);
      } else if (current) {
        current.lines.push(row);
      }
    });

    const totalScales = groups.length;
    let done = 0;

    this.isInserting = true;
    this.displayTable = false;
    this.isLoading = true;
    this.insertionMessage = 'Please wait...';

    from(groups).pipe(
      concatMap(group =>
        of(group).pipe(
          delay(800),
          concatMap(g => {
            const h = g.header;
            const toIso = (v: any): string | null => {
              if (!v) return null;
              const s = v.toString().trim();
              // Already ISO yyyy-mm-dd
              if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s;
              // MM/DD/YYYY → YYYY-MM-DD
              const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
              if (m) return `${m[3]}-${m[1].padStart(2,'0')}-${m[2].padStart(2,'0')}`;
              return s;
            };
            const toFlag = (v: any): boolean =>
              v === true || v === 'Y' || v === 'y' || v === 'true';

            const payload = {
              name:        h.name,
              description: h.description || '',
              dailyRate:   toFlag(h.isDailyRate),
              minimumWage: toFlag(h.isMinimumWage),
              allowance:   toFlag(h.isAllowance),
              lines: g.lines.map(l => ({
                ratePerPeriod:      +l.ratePerPeriod || 0,
                monthlyRate:        +l.monthlyRate || 0,
                dailyRate:          +l.dailyRate || 0,
                hourlyRate:         +l.hourlyRate || 0,
                minuteRate:         +l.minuteRate || 0,
                RAMP_Hours_Per_Day: +l.hoursPerDay || 8,
                minValue:           +l.minValue || 0,
                maxValue:           +l.maxValue || 0,
                validFrom:          toIso(l.validFrom),
                validTo:            toIso(l.validTo),
              })),
            };

            done++;
            this.insertionMessage =
              `Inserting scale ${done} of ${totalScales} with ${g.lines.length} line(s)...`;

            console.log('Scale payload:', JSON.stringify(payload, null, 2));
            return this.importDataService.insertScaleDetailsFromCSV(payload).pipe(
              tap({ error: err => console.error('Scale insert error', err) })
            );
          })
        )
      )
    ).subscribe({
      next: () => {},
      error: err => {
        console.error(err);
        this.isLoading = false;
        this.isInserting = false;
        this.insertionMessage = 'An error occurred. Please try again.';
      },
      complete: () => {
        this.insertionMessage = `Imported ${totalScales} scale(s) successfully.`;
        this.isImported  = true;
        this.isInserting = false;
        this.isLoading   = false;
      }
    });
  }

  onClose(): void {
    this.activeModal.dismiss('cancel');
    window.location.reload();
  }
}
